import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PaymentMethod, PaymentMethodInput,
  listPaymentMethods, createPaymentMethod, updatePaymentMethod, deletePaymentMethod,
} from '../api.settings';
import {
  startStripeConnect, resetStripeConnect, inviteStripeOwner, revokeStripeOwnerInvite,
} from '../api.stripeConnect';
import { useLang } from '../i18n/LanguageContext';
import { settingsPaymentMethodsText } from '../i18n/pages/settingsPaymentMethods';

const MAX_PAYMENT_METHODS = 50;
const SUPPORT_EMAIL = 'info@guestbook.cz';

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', border: '1px solid #cbd5e0',
  borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
};
const labelStyle: React.CSSProperties = { fontWeight: 600, display: 'block', marginBottom: 4, fontSize: '0.85rem' };
const linkButtonStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: '#64748b', textDecoration: 'underline',
  cursor: 'pointer', fontSize: 12, padding: 0,
};

type PMType = 'BANK_TRANSFER' | 'STRIPE';

const emptyForm = (type: PMType): PaymentMethodInput => ({
  type, name: '', displayName: '', detailsJson: '',
});

const spinner = (size: number): React.CSSProperties => ({
  width: size, height: size, border: '2px solid currentColor', borderTopColor: 'transparent',
  borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite',
});

const SettingsPaymentMethods: React.FC = () => {
  const { lang } = useLang();
  const t = settingsPaymentMethodsText[lang];
  const [searchParams, setSearchParams] = useSearchParams();
  const TYPE_LABELS: Record<string, string> = {
    BANK_TRANSFER: t.typeBankTransfer,
    STRIPE: t.typeStripe,
  };

  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // ── Add / edit form ─────────────────────────────────────────────────────
  // 'closed' → 'chooseType' (pick Bank Transfer vs Stripe) → 'form' (fill in the details)
  const [addStep, setAddStep] = useState<'closed' | 'chooseType' | 'form'>('closed');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<PaymentMethodInput>(emptyForm('BANK_TRANSFER'));
  const [saving, setSaving] = useState(false);

  // ── Per-row Stripe connect state ────────────────────────────────────────
  const [connectingId, setConnectingId] = useState<number | null>(null);
  const [resettingId, setResettingId] = useState<number | null>(null);

  // ── Per-row owner-invitation state ──────────────────────────────────────
  const [inviteFormId, setInviteFormId] = useState<number | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitingId, setInvitingId] = useState<number | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const load = async (refreshStripe = false) => {
    setLoading(true);
    setError(null);
    try {
      setMethods(await listPaymentMethods({ refresh: refreshStripe }));
    } catch {
      setError(t.loadError);
    } finally {
      setLoading(false);
    }
  };

  // Stripe sends the browser back with ?stripe=return|refresh. The server otherwise trusts a status
  // verified in the last few minutes, which would show a just-finished onboarding as still pending
  // — so that first load asks it to re-check Stripe live. The marker is then removed from the URL:
  // left there, every later remount (switching profile tabs and back, a reload) would force another
  // live Stripe round trip. Only `stripe` is dropped, so `tab` and anything else survive, and
  // `replace` keeps Back from stepping into a URL that would force a refresh again.
  // The ref makes this run once per mount: StrictMode re-runs effects in dev, and the second run
  // must neither re-read the (not yet updated) closure nor send a second request.
  const initialLoadStarted = useRef(false);
  useEffect(() => {
    if (initialLoadStarted.current) return;
    initialLoadStarted.current = true;
    const marker = searchParams.get('stripe');
    const forceRefresh = marker === 'return' || marker === 'refresh';
    if (marker !== null) {
      setSearchParams(p => { p.delete('stripe'); return p; }, { replace: true });
    }
    load(forceRefresh);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only by design, see above
  }, []);

  const atLimit = methods.length >= MAX_PAYMENT_METHODS;

  const openChooseType = () => {
    setEditingId(null);
    setAddStep('chooseType');
    setError(null);
    setSuccess(null);
  };

  const chooseType = (type: PMType) => {
    setForm(emptyForm(type));
    setAddStep('form');
  };

  const openEdit = (pm: PaymentMethod) => {
    setEditingId(pm.id);
    setForm({
      type: pm.type, name: pm.name, displayName: pm.displayName || '', detailsJson: pm.detailsJson || '',
    });
    setAddStep('form');
    setError(null);
    setSuccess(null);
  };

  const cancel = () => { setAddStep('closed'); setEditingId(null); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { setError(t.nameRequiredError); return; }
    setSaving(true);
    setError(null);
    try {
      const payload: PaymentMethodInput = {
        ...form,
        displayName: form.displayName?.trim() || null,
        detailsJson: form.type === 'BANK_TRANSFER' ? (form.detailsJson?.trim() || null) : null,
      };
      if (editingId != null) {
        await updatePaymentMethod(editingId, payload);
        setSuccess(t.updateSuccess);
      } else {
        await createPaymentMethod(payload);
        setSuccess(t.createSuccess);
      }
      setAddStep('closed');
      setEditingId(null);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || t.saveError);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pm: PaymentMethod) => {
    if (!window.confirm(t.deleteConfirm.replace('{name}', pm.name))) return;
    setError(null);
    setSuccess(null);
    try {
      await deletePaymentMethod(pm.id);
      setSuccess(t.deleteSuccess);
      await load();
    } catch (err: any) {
      // Show what the server actually said. The old fallback claimed "assigned to a booking site"
      // for every failure, which was misleading whenever the real cause was something else.
      setError(err?.response?.data?.message || t.deleteError);
    }
  };

  const handleConnect = async (pm: PaymentMethod) => {
    setConnectingId(pm.id);
    setError(null);
    try {
      const { url } = await startStripeConnect(pm.id);
      window.location.href = url;
    } catch (err: any) {
      setError(err?.response?.data?.message || t.connectStartError);
      setConnectingId(null);
    }
  };

  const handleReset = async (pm: PaymentMethod) => {
    if (!window.confirm(t.resetConfirm)) return;
    setResettingId(pm.id);
    setError(null);
    try {
      await resetStripeConnect(pm.id);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || t.resetError);
    } finally {
      setResettingId(null);
    }
  };

  const openInviteForm = (pm: PaymentMethod) => {
    setInviteFormId(pm.id);
    setInviteEmail(pm.stripeInvite?.invitedEmail || '');
    setError(null);
    setSuccess(null);
  };

  const handleInviteSend = async (pm: PaymentMethod) => {
    setInvitingId(pm.id);
    setError(null);
    try {
      await inviteStripeOwner(pm.id, inviteEmail.trim());
      setInviteFormId(null);
      setInviteEmail('');
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || t.inviteError);
    } finally {
      setInvitingId(null);
    }
  };

  const handleInviteRevoke = async (pm: PaymentMethod) => {
    if (!window.confirm(t.inviteCancelConfirm)) return;
    setError(null);
    try {
      await revokeStripeOwnerInvite(pm.id);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || t.inviteError);
    }
  };

  const handleCopyInviteLink = async (pm: PaymentMethod) => {
    const url = pm.stripeInvite?.url;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(pm.id);
      window.setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // Clipboard is blocked outside a secure context (plain http on a LAN address, for one).
      // Falling back to a prompt keeps the link reachable rather than failing silently.
      window.prompt(t.inviteCopyLink, url);
    }
  };

  const inviteForm = (pm: PaymentMethod) => (
    <div style={{ marginTop: 10, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14 }}>
      <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 4 }}>{t.inviteTitle}</div>
      <p style={{ margin: '0 0 12px', fontSize: '0.8rem', color: '#64748b' }}>{t.inviteDesc}</p>
      <label style={{ ...labelStyle, fontSize: '0.78rem' }}>{t.inviteEmailLabel}</label>
      <input
        type="email"
        value={inviteEmail}
        onChange={e => setInviteEmail(e.target.value)}
        placeholder={t.inviteEmailPlaceholder}
        style={inputStyle}
      />
      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
        <button
          onClick={() => handleInviteSend(pm)}
          disabled={invitingId === pm.id || !inviteEmail.trim()}
          style={{
            background: '#635bff', color: '#fff', border: 'none', borderRadius: 8,
            padding: '8px 16px', fontWeight: 600, fontSize: 13,
            cursor: (invitingId === pm.id || !inviteEmail.trim()) ? 'not-allowed' : 'pointer',
            opacity: (invitingId === pm.id || !inviteEmail.trim()) ? 0.6 : 1,
            display: 'inline-flex', alignItems: 'center', gap: 8,
          }}>
          {invitingId === pm.id && <span style={spinner(12)} />}
          {invitingId === pm.id ? t.inviteSending : t.inviteSend}
        </button>
        <button type="button" onClick={() => setInviteFormId(null)} style={{
          background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: 8,
          padding: '8px 16px', cursor: 'pointer', fontSize: 13,
        }}>
          {t.cancel}
        </button>
      </div>
    </div>
  );

  /** An outstanding invitation deliberately replaces the manager's own Connect button rather than
   * sitting beside it: both act on the same connected account, so a manager clicking "Finish setup"
   * here would walk into the owner's half-filled Stripe form and start entering their own details.
   * Taking over is still possible — cancel the invitation first, which makes it an explicit choice. */
  const inviteStatusBlock = (pm: PaymentMethod) => {
    const invite = pm.stripeInvite!;
    const expires = new Date(invite.expiresAt).toLocaleDateString(lang === 'en' ? 'en-GB' : lang);
    return (
      <div style={{ marginTop: 10 }}>
        <div style={{
          background: '#fefce8', border: '1px solid #fde047', color: '#854d0e',
          borderRadius: 8, padding: '8px 12px', fontSize: '0.8rem',
        }}>
          {invite.started
            ? t.inviteStartedNote
            : <>
                {t.inviteSentTo.replace('{email}', invite.invitedEmail)}
                {' · '}
                {t.inviteExpires.replace('{date}', expires)}
              </>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginTop: 10 }}>
          <button onClick={() => openInviteForm(pm)} style={linkButtonStyle}>{t.inviteResend}</button>
          <button onClick={() => handleCopyInviteLink(pm)} style={linkButtonStyle}>
            {copiedId === pm.id ? `✓ ${t.inviteCopied}` : t.inviteCopyLink}
          </button>
          <button onClick={() => handleInviteRevoke(pm)} style={linkButtonStyle}>{t.inviteCancel}</button>
        </div>
      </div>
    );
  };

  const stripeStatusBlock = (pm: PaymentMethod) => {
    const connecting = connectingId === pm.id;
    const resetting = resettingId === pm.id;

    if (pm.stripePlatformConfigured === false) {
      return (
        <div style={{ marginTop: 10, background: '#fefce8', border: '1px solid #fde047', color: '#854d0e', borderRadius: 8, padding: '8px 12px', fontSize: '0.8rem' }}>
          ⚠️ {t.stripeNotConfigured}
        </div>
      );
    }

    if (pm.stripeChargesEnabled) {
      return (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: '#166534', fontWeight: 600 }}>
            <span>✓ {t.stripeConnectedReady}</span>
            <code style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 400 }}>{pm.stripeAccountId}</code>
          </div>
          <div style={{ marginTop: 4, fontSize: '0.78rem', color: '#94a3b8' }}>
            {t.stripeConnectedHint}
          </div>
        </div>
      );
    }

    if (inviteFormId === pm.id) {
      return inviteForm(pm);
    }

    if (pm.stripeInvite) {
      return inviteStatusBlock(pm);
    }

    if (pm.stripeConnected) {
      return (
        <div style={{ marginTop: 10 }}>
          <div style={{ background: '#fefce8', border: '1px solid #fde047', color: '#854d0e', borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: '0.8rem' }}>
            {t.stripeDetailsMissingWarning}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <button onClick={() => handleConnect(pm)} disabled={connecting || resetting} style={{
              background: '#635bff', color: '#fff', border: 'none', borderRadius: 8,
              padding: '8px 16px', fontWeight: 600, cursor: (connecting || resetting) ? 'not-allowed' : 'pointer', fontSize: 13,
              opacity: connecting ? 0.7 : 1, display: 'inline-flex', alignItems: 'center', gap: 8,
            }}>
              {connecting && <span style={spinner(12)} />}
              {connecting ? t.stripeRedirecting : t.stripeFinishSetup}
            </button>
            <button onClick={() => handleReset(pm)} disabled={connecting || resetting} style={{
              background: 'none', border: 'none', color: '#64748b', textDecoration: 'underline',
              cursor: (connecting || resetting) ? 'not-allowed' : 'pointer', fontSize: 12, padding: 0,
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              {resetting && <span style={spinner(10)} />}
              {resetting ? t.stripeResetting : t.stripeResetLink}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button onClick={() => handleConnect(pm)} disabled={connecting} style={{
          background: '#635bff', color: '#fff', border: 'none', borderRadius: 8,
          padding: '8px 16px', fontWeight: 600, cursor: connecting ? 'not-allowed' : 'pointer', fontSize: 13,
          opacity: connecting ? 0.7 : 1, display: 'inline-flex', alignItems: 'center', gap: 8,
        }}>
          {connecting && <span style={spinner(12)} />}
          {connecting ? t.stripeRedirecting : t.stripeConnectCta}
        </button>
        <button onClick={() => openInviteForm(pm)} disabled={connecting} style={{
          background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 8,
          padding: '8px 16px', fontWeight: 600, cursor: connecting ? 'not-allowed' : 'pointer', fontSize: 13,
        }}>
          {t.inviteOwnerCta}
        </button>
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700 }}>{t.pageTitle}</h2>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.85rem' }}>
            {t.pageDesc}
            {methods.length > 0 && ` (${methods.length}/${MAX_PAYMENT_METHODS})`}
          </p>
        </div>
        {addStep === 'closed' && !atLimit && (
          <button onClick={openChooseType} style={{
            background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8,
            padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 14, flexShrink: 0,
          }}>
            {t.addButton}
          </button>
        )}
      </div>

      {atLimit && addStep === 'closed' && (
        <div style={{ background: '#fefce8', border: '1px solid #fde047', color: '#854d0e', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: '0.85rem' }}>
          {t.limitWarning.replace('{limit}', String(MAX_PAYMENT_METHODS)).replace('{email}', SUPPORT_EMAIL)}
        </div>
      )}

      {error && <div style={{ background: '#fef2f2', color: '#dc2626', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 14 }}>{error}</div>}
      {success && <div style={{ background: '#f0fdf4', color: '#16a34a', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 14 }}>{success}</div>}

      {addStep === 'chooseType' && (
        <div className="settings-pad-lg" style={{ background: '#f8fafc', borderRadius: 10, marginBottom: 20, border: '1px solid #e2e8f0' }}>
          <h3 style={{ margin: '0 0 14px', fontSize: '1rem', fontWeight: 700 }}>{t.chooseTypeTitle}</h3>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button onClick={() => chooseType('BANK_TRANSFER')} style={{
              flex: '1 1 200px', textAlign: 'left', background: '#fff', border: '2px solid #e2e8f0', borderRadius: 10,
              padding: 16, cursor: 'pointer', fontSize: 14,
            }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>{t.bankTransferCardTitle}</div>
              <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{t.bankTransferCardDesc}</div>
            </button>
            <button onClick={() => chooseType('STRIPE')} style={{
              flex: '1 1 200px', textAlign: 'left', background: '#fff', border: '2px solid #e2e8f0', borderRadius: 10,
              padding: 16, cursor: 'pointer', fontSize: 14,
            }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>{t.stripeCardTitle}</div>
              <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{t.stripeCardDesc}</div>
            </button>
          </div>
          <button type="button" onClick={cancel} style={{
            marginTop: 14, background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 13, padding: 0,
          }}>
            {t.cancel}
          </button>
        </div>
      )}

      {addStep === 'form' && (
        <form onSubmit={handleSubmit} className="settings-pad-lg" style={{ background: '#f8fafc', borderRadius: 10, marginBottom: 20, border: '1px solid #e2e8f0' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 700 }}>
            {editingId != null ? t.editFormTitle : `${t.newFormTitlePrefix} ${TYPE_LABELS[form.type]}`}
          </h3>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>{t.fieldInternalName} <span style={{ color: '#dc2626' }}>*</span></label>
            <input
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder={form.type === 'BANK_TRANSFER' ? t.placeholderInternalNameBank : t.placeholderInternalNameStripe}
              style={inputStyle}
            />
            <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: '#94a3b8' }}>{t.hintInternalName}</p>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>{t.fieldDisplayName}</label>
            <input
              value={form.displayName || ''}
              onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
              placeholder={t.placeholderDisplayName}
              style={inputStyle}
            />
            <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: '#94a3b8' }}>{t.hintDisplayName}</p>
          </div>

          {form.type === 'BANK_TRANSFER' && (
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>{t.fieldAccountDetails}</label>
              <textarea
                value={form.detailsJson || ''}
                onChange={e => setForm(f => ({ ...f, detailsJson: e.target.value }))}
                placeholder={t.placeholderAccountDetails}
                rows={3}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </div>
          )}

          {form.type === 'STRIPE' && editingId == null && (
            <p style={{ margin: '0 0 18px', fontSize: '0.78rem', color: '#94a3b8' }}>
              {t.stripeNewMethodNote}
            </p>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button type="submit" disabled={saving} style={{
              background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8,
              padding: '8px 20px', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14,
            }}>
              {saving ? t.saving : editingId != null ? t.saveChanges : t.add}
            </button>
            <button type="button" onClick={cancel} style={{
              background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: 8,
              padding: '8px 16px', cursor: 'pointer', fontSize: 14,
            }}>
              {t.cancel}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p style={{ color: '#64748b' }}>{t.loading}</p>
      ) : methods.length === 0 ? (
        <p style={{ color: '#64748b', textAlign: 'center', padding: '24px 0' }}>
          {t.emptyState}
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {methods.map(pm => (
            <div key={pm.id} className="settings-pad-md" style={{
              border: '1px solid #e2e8f0', borderRadius: 12,
              background: '#fff',
            }}>
              {/* Top row: type badge + actions */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
                <span style={{
                  fontSize: '0.7rem', fontWeight: 700, padding: '3px 9px', borderRadius: 20,
                  background: pm.type === 'BANK_TRANSFER' ? '#dbeafe' : '#f3e8ff',
                  color: pm.type === 'BANK_TRANSFER' ? '#1e40af' : '#6b21a8',
                  whiteSpace: 'nowrap',
                }}>
                  {TYPE_LABELS[pm.type] || pm.type}
                </span>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => openEdit(pm)} title={t.editTooltip} style={{
                    background: '#f1f5f9', border: 'none', borderRadius: 7,
                    width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 15, color: '#475569',
                  }}>✎</button>
                  <button onClick={() => handleDelete(pm)} title={t.deleteTooltip} style={{
                    background: '#fef2f2', border: 'none', borderRadius: 7,
                    width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 15, color: '#dc2626',
                  }}>✕</button>
                </div>
              </div>

              {/* Internal name */}
              <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>{pm.name}</div>

              {/* Display name */}
              {pm.displayName ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8' }}>{t.forGuestLabel}</span>
                  <span style={{ fontSize: '0.88rem', color: '#475569' }}>{pm.displayName}</span>
                </div>
              ) : (
                <div style={{ fontSize: '0.8rem', color: '#cbd5e0', marginBottom: 6, fontStyle: 'italic' }}>{t.displayNameUnset}</div>
              )}

              {/* Details */}
              {pm.detailsJson && (
                <pre style={{ margin: 0, fontSize: '0.8rem', color: '#64748b', whiteSpace: 'pre-wrap', fontFamily: 'inherit', background: '#f8fafc', borderRadius: 6, padding: '8px 10px' }}>
                  {pm.detailsJson}
                </pre>
              )}

              {pm.type === 'STRIPE' && stripeStatusBlock(pm)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SettingsPaymentMethods;
